import {
  EVENTS_TEMPLATE_ID,
  EVENT_QUERY_STRINGS,
  EVENT_TITLE_DICTIONARY_KEY,
} from '../../../constants/eventsFairs.constants';
import { updateSearchResults } from '../../../hooks/useQueryParams.effect';
import { getCanUseDate } from '../../../utils/eventsListings.utils';
import { TBaseObj, TIconField, TImageField, TLinkField } from '../../../types/types';
import { appendToUrlPath } from '../../../utils/linkUtils';
import { windowService } from '../../../utils/window.utils';
import {
  TEventCardProps,
  EventListingFilters,
  TEventsListingFields,
  TEventsListingItem,
  TEventsListingItemData,
  TEventsListingProps,
} from '../types/eventsListing.types';
import { TSaveCalendarEventProps } from '../../@shared/saveCalendarEvent/types/saveEvent.types';

export class EventsListingItemModel {
  private readonly eventItem?: TEventsListingItem;
  readonly itemData?: TEventsListingItemData;

  constructor(props: TEventCardProps) {
    this.eventItem = props;
    this.itemData = props.itemData || {};
  }

  get desktopImage(): TImageField | undefined {
    return this.eventItem?.Image;
  }

  get mobileImage(): TImageField | undefined {
    return this.eventItem?.MobileImage;
  }

  get eventTimeline(): TEventsListingItem['eventTimeline'] {
    return this.eventItem?.eventTimeline;
  }

  get eventStatusDictionaryKey(): string | undefined {
    if (this.eventTimeline === 'UPCOMING') return 'cnhi-event-upcoming';
    if (this.eventTimeline === 'ONGOING') return 'cnhi-event-ongoing';
    if (this.eventTimeline === 'PAST') return 'cnhi-event-past';

    return undefined;
  }

  get templateId(): EVENTS_TEMPLATE_ID | undefined {
    return this.eventItem?.templateId || undefined;
  }

  get templateName(){
    return this.eventItem?.templateName;
  }

  get eventTypeDetails(): { eventTypeDictionary: string; eventIconField?: TIconField } | undefined {
    if (this.templateId === EVENTS_TEMPLATE_ID.EVENT)
      return { eventTypeDictionary: EVENT_TITLE_DICTIONARY_KEY.EVENT, eventIconField: this.itemData?.fields?.eventIcon };
    if (this.templateId === EVENTS_TEMPLATE_ID.FAIR)
      return { eventTypeDictionary: EVENT_TITLE_DICTIONARY_KEY.FAIR, eventIconField: this.itemData?.fields?.fairIcon };
    if (this.templateId === EVENTS_TEMPLATE_ID.LIVE_EVENT)
      return { eventTypeDictionary: EVENT_TITLE_DICTIONARY_KEY.LIVE_EVENT, eventIconField: this.itemData?.fields?.liveEventIcon };
    return undefined;
  }

  get eventStartDate(): string | undefined {
    return this.eventItem?.startDate;
  }

  get canShowStartDate(): boolean {
    return getCanUseDate(this.eventItem?.startDate);
  }

  get eventEndDate(): string | undefined {
    return this.eventItem?.endDate;
  }

  get canShowEndDate(): boolean {
    return getCanUseDate(this.eventItem?.endDate);
  }

  get calendarDetails(): TSaveCalendarEventProps['calendarDetails'] {
    return this.eventItem?.calendarDetails || [];
  }

  get time(): string | undefined {
    return this.eventItem?.time;
  }

  get location(): string | undefined {
    return this.eventItem?.location;
  }

  get title(): string | undefined {
    return this.eventItem?.title;
  }

  get shortDescription(): string | undefined {
    return this.eventItem?.shortDescription;
  }

  get showDetails(): boolean {
    return !!this.eventItem?.showDetails;
  }

  get ctaLink(): string | undefined {
    return this.eventItem?.link;
  }

  get primaryCTA(): TLinkField | undefined {
    return this.eventItem?.PrimaryCTA;
  }

  get canShowPrimaryCTA(): boolean {
    return !!(this.primaryCTA?.value?.href && this.primaryCTA?.value?.text);
  }

  get shareLink(): string | undefined {
    const origin = windowService()?.location?.origin;
    return origin ? appendToUrlPath(origin, this.ctaLink || '') : '';
  }
}

export class EventsListingModel {
  private readonly fields?: TEventsListingFields;

  constructor(props: TEventsListingProps) {
    this.fields = props.fields;
  }

  get items(): TEventsListingItem[] {
    return this.fields?.apiData?.items || [];
  }

  get itemData(): TEventsListingItemData {
    return this.fields?.itemData || {};
  }

  resetFilter(navigate) {
    updateSearchResults(navigate, {
      [EVENT_QUERY_STRINGS.EVENT_PAGE]: '',
      [EVENT_QUERY_STRINGS.EVENT_TIMELINE]: '',
      [EVENT_QUERY_STRINGS.EVENT_TYPE]: '',
    });
  }

  updateQueryByKey(navigate, key, val) {
    const resetPage = key != EVENT_QUERY_STRINGS.EVENT_PAGE;

    const keyValues = resetPage
      ? {
          [EVENT_QUERY_STRINGS.EVENT_PAGE]: '',
          [key]: val || '',
        }
      : {
          [key]: val || '',
        };

    updateSearchResults(navigate, keyValues);
  }

  updateQueryByBulkKeys(navigate, obj: TBaseObj) {
    updateSearchResults(navigate, obj);
  }
  
  canShowFilter(filterName: EventListingFilters): boolean {
    let filtersTobeShownList = this.fields?.itemData?.fields?.hideFilters?.map((_) => _.fields?.Key?.value) || [];
    return !filtersTobeShownList.includes(filterName);
  }

  get counter(): number {
    return this.fields?.apiData?.totalSearchResults || 0;
  }

  get contextItemUrl(): string {
    return this.fields?.itemData?.contextItemUrl || '';
  }

  get isUnreleased(): boolean | undefined {
    return !!this.fields?.isComponentUnreleased;
  }
}
